import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  // The summary (with multi-buy savings, delivery note, express buttons and Continue shopping)
  // stays pinned until it passes two thirds of the drawer, so Check out remains visible on
  // 768px-tall laptop screens
  #summaryThreshold = 0.67;

  // The pin is measured once per cart update, but the first add to an empty bag morphs the drawer
  // inside a view transition, so that measurement runs before the summary exists and the bag
  // stays unpinned. Re-measure whenever the drawer's content is re-rendered or the summary or
  // drawer changes size (one measurement per frame at most).
  /** @type {MutationObserver | null} */
  #contentObserver = null;
  /** @type {ResizeObserver | null} */
  #sizeObserver = null;
  /** @type {Element | null} */
  #observedSummary = null;
  #stickyFrame = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    const dialog = this.#dialog;
    if (dialog) {
      this.#sizeObserver = new ResizeObserver(() => this.#scheduleStickyUpdate());
      this.#sizeObserver.observe(dialog);
      this.#contentObserver = new MutationObserver(() => this.#scheduleStickyUpdate());
      this.#contentObserver.observe(dialog, { childList: true, subtree: true });
      this.#scheduleStickyUpdate();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.#contentObserver?.disconnect();
    this.#sizeObserver?.disconnect();
    this.#observedSummary = null;
    cancelAnimationFrame(this.#stickyFrame);
    this.#stickyFrame = 0;
  }

  #scheduleStickyUpdate() {
    if (this.#stickyFrame) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = 0;
      const summary = this.#dialog?.querySelector('.cart-drawer__summary') ?? null;
      if (summary !== this.#observedSummary) {
        if (this.#observedSummary) this.#sizeObserver?.unobserve(this.#observedSummary);
        if (summary) this.#sizeObserver?.observe(summary);
        this.#observedSummary = summary;
      }
      this.#updateStickyState();
    });
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // MLB: open on the tap even when the bag is empty. Horizon opened an empty bag only once the add and a cart refresh
    // had come back (~1.5 s, more on mobile data); now it opens at once with an "adding" state over the empty-bag
    // message (mlb-luxe.css), which the new contents replace as soon as they arrive. A failed add closes it again.
    // From the quick-add dialog on phones and tablets (the bag is a modal there) it also opens at once, sliding in over
    // the dialog, which then closes unseen underneath; waiting for that dialog to fade out first showed the bare page
    // for a moment before the bag (17 Oct 2026). Focus returns to the product card that opened the quick add. From
    // other modals, and beside the page on wide screens, it still waits for that dialog to close, as below, so focus
    // is restored correctly.
    const fromQuickAdd = Boolean(sourceModal?.closest('quick-add-dialog')) && Boolean(this.#themeDrawer?.opensAsModal);
    const openNow = shouldAutoOpen && (!sourceModal || fromQuickAdd);
    const adding = openNow && this.#isCartEmpty();
    if (adding) {
      this.setAttribute('data-adding', '');
      this.setAttribute('aria-busy', 'true');
      this.#showPreview(event.target instanceof Element ? event.target : null);
      this.#clearWhenFilled();
    }
    if (openNow) {
      const quickAddOpener = fromQuickAdd
        ? /** @type {HTMLElement | null} */ (document.querySelector('quick-add-component[stay-visible] button'))
        : null;
      this.#themeDrawer?.open();
      this.#themeDrawer?.setReturnFocus(quickAddOpener);
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());
        if (adding) this.#endAdding(Boolean(detail?.didError));
        // The quick-add dialog stays open on a failed add and shows the error: take the bag back off it
        if (fromQuickAdd && openNow && detail?.didError) this.#themeDrawer?.close();

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (adding) this.#endAdding(true);
        if (fromQuickAdd && openNow) this.#themeDrawer?.close();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * MLB: the bag's contents can arrive before the cart event settles (product-form.js announces the add response's
   * rendered bag before its cart refresh), so clear the "adding" state the moment the empty bag is gone, a frame at a
   * time, for at most 10 s; #endAdding still settles it (and closes the bag) when the add fails.
   */
  #clearWhenFilled() {
    const started = performance.now();
    const check = () => {
      if (!this.hasAttribute('data-adding')) return;
      if (!this.#isCartEmpty()) {
        this.removeAttribute('data-adding');
        this.removeAttribute('aria-busy');
        this.querySelector('.mlb-bag-preview')?.remove();
        return;
      }
      if (performance.now() - started < 10000) requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  }

  /**
   * MLB: ends the "adding" state of a bag opened on the tap. The cart sections are re-rendered from the same event, so
   * wait (a frame at a time, 2 s at most) until the empty-bag markup is gone; if the bag is still empty then (the add
   * failed), close it again: the product form shows the error.
   * @param {boolean} failed
   */
  #endAdding(failed) {
    const started = performance.now();
    const finish = () => {
      this.removeAttribute('data-adding');
      this.removeAttribute('aria-busy');
      this.querySelector('.mlb-bag-preview')?.remove();
      if (this.#isCartEmpty()) this.#themeDrawer?.close();
    };
    const wait = () => {
      if (failed || !this.#isCartEmpty() || performance.now() - started > 2000) {
        finish();
        return;
      }
      requestAnimationFrame(wait);
    };
    wait();
  }

  /**
   * MLB: while the first item is being added, the bag shows that item at once, in the real bag's layout (photo, short
   * name, colour and size in the shopper's size system, price), with soft placeholders for the quantity and the offer
   * card, until the bag's contents replace it. Read from the product being added (its section or quick-add dialog);
   * any part not found becomes a placeholder too. Built with DOM APIs (text only, no HTML strings).
   * @param {Element | null} source - the element that dispatched the cart event (the product form)
   */
  #showPreview(source) {
    const holder = this.querySelector('.cart-drawer--empty');
    if (!holder || holder.querySelector('.mlb-bag-preview')) return;
    const root = source?.closest('quick-add-dialog, .shopify-section') ?? document;

    // the product page's h1; the quick-add dialog has no h1, its title is a link
    const fullTitle = root.querySelector('h1, .product-header a, .view-product-title a')?.textContent?.trim() ?? '';
    // as snippets/display-product-title.liquid: no quote marks, no X9X range name
    const name =
      fullTitle
        .replace(/[‘’'"“”]/g, '')
        .replace(/ X9X (Sneakers|Sneaker|Moon Boots|Wave Runners|Boots)$/, '')
        .trim() || fullTitle;

    const system = document.documentElement.dataset.sizeSystem || 'uk';
    const options = [...root.querySelectorAll('variant-picker input[type="radio"]:checked')].map((input) => {
      const value = /** @type {HTMLInputElement} */ (input).value;
      const parts = value.split(' - ');
      if (parts.length === 3 && value.includes('EU ') && value.includes(' - UK ')) {
        const index = { eu: 0, uk: 1, us: 2 }[system] ?? 1;
        return (parts[index] ?? parts[1]).split(' (')[0];
      }
      return value;
    });

    const price = root.querySelector('product-price .price')?.textContent?.trim().split(/\s{2,}/)[0] ?? '';
    const galleryImage = /** @type {HTMLImageElement | null} */ (
      root.querySelector('media-gallery slideshow-slide[aria-hidden="false"] img, media-gallery img')
    );
    const imageUrl = galleryImage?.currentSrc || galleryImage?.src || '';

    /** @param {string} tag @param {string} className @param {string} [text] */
    const el = (tag, className, text) => {
      const node = document.createElement(tag);
      node.className = className;
      if (text) node.textContent = text;
      return node;
    };

    // spaced like the bag's own line: "Obsidian Black", a dot with 0.4rem either side, then " UK 8"
    /** @param {string[]} values */
    const variantLine = (values) => {
      const line = el('p', 'mlb-bag-preview__variant');
      values.forEach((value, index) => {
        if (index) line.append(el('span', 'mlb-bag-preview__sep', '·'), ' ');
        line.append(value);
      });
      return line;
    };

    const preview = el('div', 'mlb-bag-preview');
    preview.setAttribute('aria-hidden', 'true');
    preview.append(el('span', 'mlb-bag-preview__bar'), el('p', 'mlb-bag-preview__title', this.#bagTitle()));

    const line = el('div', 'mlb-bag-preview__line');
    if (imageUrl) {
      // the photo on a stone tile behind it, which it multiplies onto as in the bag itself
      const tile = el('span', 'mlb-bag-preview__image');
      const image = /** @type {HTMLImageElement} */ (el('img', 'mlb-bag-preview__photo'));
      image.src = imageUrl;
      image.alt = '';
      tile.append(image);
      line.append(tile);
    } else {
      line.append(el('span', 'mlb-bag-preview__image mlb-bag-preview__skeleton'));
    }
    const details = el('div', 'mlb-bag-preview__details');
    details.append(
      name ? el('p', 'mlb-bag-preview__name', name) : el('span', 'mlb-bag-preview__name mlb-bag-preview__skeleton'),
      options.length ? variantLine(options) : el('span', 'mlb-bag-preview__variant mlb-bag-preview__skeleton'),
      el('span', 'mlb-bag-preview__qty mlb-bag-preview__skeleton')
    );
    line.append(details, price ? el('p', 'mlb-bag-preview__price', price) : el('span', 'mlb-bag-preview__price'));
    // the offer card's outline: title, three-step bar, three suggestion tiles
    const card = el('div', 'mlb-bag-preview__card');
    const steps = el('div', 'mlb-bag-preview__steps');
    const tiles = el('div', 'mlb-bag-preview__tiles');
    for (let i = 0; i < 3; i++) {
      steps.append(el('span', 'mlb-bag-preview__skeleton'));
      const tile = el('div', 'mlb-bag-preview__tile');
      tile.append(el('span', 'mlb-bag-preview__skeleton'), el('span', 'mlb-bag-preview__skeleton'), el('span', 'mlb-bag-preview__skeleton'));
      tiles.append(tile);
    }
    card.append(el('span', 'mlb-bag-preview__card-title mlb-bag-preview__skeleton'), steps, tiles);
    preview.append(line, card);

    holder.append(preview);
  }

  /** The bag's own title ("Bag", translated), printed on the element by snippets/cart-drawer.liquid */
  #bagTitle() {
    return this.dataset.bagTitle || 'Bag';
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// On wider screens the bag opens beside the page as a non-modal dialog, so there is no
// backdrop to tap. Close it when the shopper clicks anywhere outside it, as on mobile.
// Clicks that open or feed the bag (bag icon, Add to bag, express checkout, other dialogs)
// are left alone.
document.addEventListener('click', (event) => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || !('isOpen' in drawer) || !drawer.isOpen) return;

  const panel = drawer.querySelector('dialog');
  if (!panel || panel.matches(':modal')) return;

  const target = event.target instanceof Element ? event.target : null;
  if (!target || !target.isConnected || panel.contains(target)) return;

  const keepsBagOpen =
    '[on\\:click*="#cart-drawer"], add-to-cart-component, quick-add-component, dialog[open], ' +
    'shopify-accelerated-checkout, .shopify-payment-button, .sticky-add-to-cart';
  if (target.closest(keepsBagOpen)) return;

  /** @type {{ close: () => void }} */ (/** @type {unknown} */ (drawer)).close();
});
